import { breakpoints, Inline, Stack, Text, textColors, textVariants, vars } from '@etg/wings';
import { CheckCircle } from '@phosphor-icons/react';
import { Icon } from '@eti/components';
import { css } from '@eti/styles';
import { useProperty, useTranslation } from '@eti/providers';
import { Fragment } from 'react';
import type { IncludedCabinBaggageType as RebookingTripIncludedCabinBaggageType } from '../../../../../change-trip/models';
import type { IncludedCabinBaggageType, IncludedCheckedBaggageType } from '../../../../models';
import CheckedBaggageSelfTransferCard from './CheckedBaggageSelfTransferCard';

const baggageSectionStyles = css`
  flex-basis: 0;
  flex-grow: 2;

  @media (max-width: ${breakpoints._768}) {
    width: 100%;
  }
`;

const periodStyles = css`
  background: #737373;
  border-radius: 50%;
  display: inline-block;
  height: 3px;
  margin: 3px 5px;
  width: 3px;
`;

const baggageIconAndInfoSectionStyles = css`
  padding-inline-start: 24px;
`;

const iconWrapperStyles = css`
  background-color: ${vars.colors.signal.information.background};
  border-radius: 8px;
  height: 28px;
  padding: 4px;
`;

const baggageSizeStyles = css`
  gap: 2px;
`;

type BaggageData = {
  pieces?: number | null;
  size3d?: { height: number; length: number; width: number } | null;
  weight?: number | null;
  weightUnit?: string | null;
};

export interface IncludedBaggageProps {
  includedCabinBaggage: IncludedCabinBaggageType | RebookingTripIncludedCabinBaggageType;
  includedCheckedBaggage?: IncludedCheckedBaggageType | null;
  isResultCardBaggageEnabled: boolean;
  isVI: boolean;
}

const IncludedBaggage = ({
  includedCabinBaggage,
  includedCheckedBaggage,
  isResultCardBaggageEnabled,
  isVI,
}: IncludedBaggageProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const isVISelfTansferDealEnabled = p('IbeClient.VI.SelfTransferDeal.Enabled');

  const formatBaggageInfo = (iconId: string, name: string, baggageData?: BaggageData) => ({
    icon: iconId,
    dataTestId: iconId,
    name,
    pieces: baggageData?.pieces ?? null,
    size: baggageData?.size3d ? (
      <Text color={textColors.SECONDARY} variant={textVariants._12}>
        <bdi className={baggageSizeStyles}>
          ({baggageData?.size3d?.length} x {baggageData?.size3d?.width} x{' '}
          {baggageData?.size3d?.height})
        </bdi>
      </Text>
    ) : null,
    weight:
      baggageData?.weight && baggageData.weight > 0 ? (
        <Text color={textColors.SECONDARY} variant={textVariants._12}>
          <bdi>
            {baggageData.weight} {baggageData.weightUnit}
          </bdi>
        </Text>
      ) : null,
  });

  const baggageInfoMap = {
    personalItem: includedCabinBaggage.includedPersonalItem
      ? formatBaggageInfo('personal-item', t('Result.Baggage.PersonalItem.Text'), {
          pieces: 1,
        })
      : null,
    cabinBaggage: includedCabinBaggage.pieces
      ? formatBaggageInfo('cabin-baggage', t('Result.Baggage.CabinBaggage.Text'), {
          pieces: includedCabinBaggage.pieces,
          size3d: includedCabinBaggage.size3d,
          weight: includedCabinBaggage.weight,
          weightUnit: includedCabinBaggage.weightUnit,
        })
      : null,
    checkedBaggage: includedCheckedBaggage?.weight
      ? formatBaggageInfo('check-in-baggage', t('Result.Baggage.CheckedBaggage.Text'), {
          pieces: includedCheckedBaggage.pieces,
          weight: includedCheckedBaggage.weight,
          weightUnit: includedCheckedBaggage.weightUnit,
        })
      : null,
  };

  const personalItemAnnouncement = includedCabinBaggage.includedPersonalItem
    ? `,1 ${t('Result.Baggage.PersonalItem.Text')}`
    : '';

  const cabinBaggagePiecesAnnouncement = includedCabinBaggage.pieces
    ? `,${includedCabinBaggage.pieces} ${t('Result.Baggage.CabinBaggage.Text')}`
    : '';

  const cabinBaggageSizeAnnouncement = includedCabinBaggage.size3d
    ? `,${t(
        'ScreenReader.ResultCard.TripBaggageInfo.BaggageDimensions.AriaLabel',
        includedCabinBaggage.size3d.length,
        includedCabinBaggage.size3d.width,
        includedCabinBaggage.size3d.height,
      )}`
    : '';

  const cabinBaggageWeightAnnouncement = includedCabinBaggage.weight
    ? `, ${includedCabinBaggage.weight} ${includedCabinBaggage.weightUnit}`
    : '';

  const checkedBaggageAnnouncement = includedCheckedBaggage
    ? `,${includedCheckedBaggage.pieces} ${t('Result.Baggage.CheckedBaggage.Text')}, ${
        includedCheckedBaggage.weight
      } ${includedCheckedBaggage.weightUnit}`
    : '';

  const stackAriaLabel = `${t(
    'Result.Baggage.Included.Text',
  )}${personalItemAnnouncement}${cabinBaggagePiecesAnnouncement}${cabinBaggageSizeAnnouncement}${cabinBaggageWeightAnnouncement}${checkedBaggageAnnouncement}`;

  return (
    <>
      <Stack
        aria-label={stackAriaLabel}
        className={baggageSectionStyles}
        data-testid="included-section"
        spacing={8}
      >
        <Inline align="start" alignY="center" aria-hidden noWrap spacing={4}>
          <CheckCircle color={vars.colors.signal.positive.foreground} size="20" weight="fill" />
          <Text variant={textVariants._14_BOLD}>{t('Result.Baggage.Included.Text')}</Text>
        </Inline>

        {Object.entries(baggageInfoMap).map(
          ([key, baggage]) =>
            baggage && (
              <Fragment key={key}>
                <Inline
                  align="start"
                  aria-hidden
                  className={baggageIconAndInfoSectionStyles}
                  data-testid={baggage.dataTestId}
                  key={`included-baggage-${key}`}
                  noWrap
                  spacing={4}
                >
                  {isResultCardBaggageEnabled ? (
                    <Inline alignY="center" className={iconWrapperStyles} noWrap spacing={4}>
                      {baggage.pieces && (
                        <Text variant={textVariants._14_BOLD}>{baggage.pieces}</Text>
                      )}
                      <Icon id={baggage.icon} size="20px" />
                    </Inline>
                  ) : (
                    <Icon id={baggage.icon} size="20px" />
                  )}

                  <Stack spacing={0}>
                    <Inline spacing={4}>
                      {isResultCardBaggageEnabled ? (
                        <>
                          {baggage.pieces && <Text>x</Text>}
                          <Text>{baggage.name}</Text>
                        </>
                      ) : (
                        <>
                          {baggage.pieces && (
                            <>
                              <Text>{baggage.pieces}</Text>
                              <Text>x</Text>
                            </>
                          )}
                          <Text>{baggage.name}</Text>
                        </>
                      )}
                    </Inline>
                    <Text>
                      {baggage.size}
                      {baggage.size && baggage.weight && <span className={periodStyles} />}
                      {baggage.weight}
                    </Text>
                  </Stack>
                </Inline>
              </Fragment>
            ),
        )}
      </Stack>
      {isVISelfTansferDealEnabled && isVI && includedCheckedBaggage?.weight && (
        <CheckedBaggageSelfTransferCard />
      )}
    </>
  );
};

export default IncludedBaggage;
